// src/components/landing/landing-mixin.ts
import { LitElement } from "lit";
import { state } from "lit/decorators.js";
import {
  RECORD_START_SOUND,
  RECORD_STOP_SOUND,
  SEND_SOUND,
} from "../../config";

/**
 * Shared mixin for landing components that contains all reusable logic
 * for message handling, audio playback, recording, and formatting.
 */
export const LandingMixin = <T extends Constructor<LitElement>>(
  superClass: T
) => {
  class LandingMixinClass extends superClass {
    @state() protected message = "";
    @state() protected showRecorder = false;
    @state() protected recordingSeconds = 0;
    protected get waveformBarCount() {
      return 32;
    }
    @state() protected waveformLevels: number[] = Array(32).fill(4);
    @state() protected copiedMessageId: string | null = null;
    @state() protected playingAudioMessageId: string | null = null;
    @state() protected audioMessageStates: Record<
      string,
      "playing" | "paused" | "none"
    > = {};
    protected recordingInterval: number | undefined;
    protected audioCheckInterval: number | undefined;

    protected onInput(e: Event) {
      const target = e.target as HTMLInputElement | HTMLTextAreaElement;
      this.message = target.value;
    }

    protected send() {
      if (!this.message.trim()) return;
      this.playSoundEffect("send");
      this.dispatchEvent(
        new CustomEvent("send-text", {
          detail: { text: this.message.trim() },
          bubbles: true,
          composed: true,
        })
      );
      this.message = "";
    }

    protected requestAudio(chatMessageId: string) {
      this.dispatchEvent(
        new CustomEvent("request-audio", {
          detail: { chatMessageId },
          bubbles: true,
          composed: true,
        })
      );
    }

    protected isAudioPlaying(messageId: string): boolean {
      return this.playingAudioMessageId === messageId;
    }

    protected getAudioState(messageId: string): "playing" | "paused" | "none" {
      return this.audioMessageStates[messageId] || "none";
    }

    protected checkAudioPlayingState() {
      const audioPlayers =
        this.shadowRoot?.querySelectorAll("hollo-audio-player");
      const nextStates: Record<string, "playing" | "paused" | "none"> = {};
      let foundPlayingId: string | null = null;

      audioPlayers?.forEach((player) => {
        try {
          const playerEl = player as any;
          const forMessageId = playerEl.forMessageId;
          if (!forMessageId) return;

          const audioEl = (player as HTMLElement).shadowRoot?.querySelector(
            "audio"
          ) as HTMLAudioElement;

          if (audioEl) {
            if (!audioEl.paused && !audioEl.ended) {
              nextStates[forMessageId] = "playing";
              foundPlayingId = forMessageId;
            } else if (
              audioEl.paused &&
              !audioEl.ended &&
              audioEl.currentTime > 0
            ) {
              nextStates[forMessageId] = "paused";
            } else {
              nextStates[forMessageId] = "none";
            }
          }
        } catch (e) {
          // Shadow DOM access might fail
        }
      });

      // Update states if they changed (simple shallow-ish comparison)
      const hasChanged =
        Object.keys(nextStates).length !==
          Object.keys(this.audioMessageStates).length ||
        Object.keys(nextStates).some(
          (key) => nextStates[key] !== this.audioMessageStates[key]
        );

      if (hasChanged) {
        this.audioMessageStates = nextStates;
      }

      if (foundPlayingId !== this.playingAudioMessageId) {
        this.playingAudioMessageId = foundPlayingId;
      }
    }

    protected openRecorder() {
      if (this.showRecorder) return;
      this.showRecorder = true;
      this.recordingSeconds = 0;
      this.recordingInterval = window.setInterval(() => {
        this.recordingSeconds += 1;
      }, 1000);
      this.playSoundEffect("start");
      this.updateWaveformLevels();
    }

    protected playSoundEffect(type: "start" | "stop" | "send") {
      try {
        let url = "";
        switch (type) {
          case "start":
            url = RECORD_START_SOUND;
            break;
          case "stop":
            url = RECORD_STOP_SOUND;
            break;
          case "send":
            url = SEND_SOUND;
            break;
        }
        if (!url) return;
        const audio = new Audio(url);
        audio.volume = 0.4;
        audio.play().catch(() => {});
      } catch (e) {
        console.warn("Failed to play sound effect", e);
      }
    }

    protected updateWaveformLevels() {
      if (!this.showRecorder) return;
      this.waveformLevels = this.getWaveformLevels();
      requestAnimationFrame(() => this.updateWaveformLevels());
    }

    protected closeRecorder = () => {
      if (this.showRecorder) {
        this.playSoundEffect("stop");
      }
      this.showRecorder = false;
      if (this.recordingInterval) {
        clearInterval(this.recordingInterval);
        this.recordingInterval = undefined;
      }
    };

    protected onSendAudio(
      _e: CustomEvent<{ base64: string; mimeType: string }>
    ) {
      if (this.showRecorder) {
        this.playSoundEffect("send");
      }
      this.showRecorder = false;
      if (this.recordingInterval) {
        clearInterval(this.recordingInterval);
        this.recordingInterval = undefined;
      }
    }

    protected triggerRecorderSend() {
      const rec = this.shadowRoot?.querySelector(
        "hollo-inline-recorder"
      ) as unknown as { sendNow?: () => Promise<void> } | null;
      if (rec && typeof rec.sendNow === "function") {
        try {
          void rec.sendNow();
        } catch {}
      }
    }

    protected getWaveformLevels() {
      const rec = this.shadowRoot?.querySelector(
        "hollo-inline-recorder"
      ) as unknown as { levels?: number[] } | null;
      return rec?.levels || Array(this.waveformBarCount).fill(4);
    }

    protected isUserTalking(): boolean {
      const avgLevel =
        this.waveformLevels.reduce((sum, level) => sum + level, 0) /
        this.waveformLevels.length;
      return avgLevel > 12;
    }

    protected formatSeconds(total: number) {
      const m = Math.floor(total / 60);
      const s = total % 60;
      const mm = String(m).padStart(2, "0");
      const ss = String(s).padStart(2, "0");
      return `${mm}:${ss}`;
    }

    protected formatTimestamp(created_at?: number | string): string {
      if (!created_at) return "";

      let date: Date;
      if (typeof created_at === "number") {
        date = new Date(created_at * 1000);
      } else {
        // Assume string
        let dateStr = String(created_at);
        // If it looks like "YYYY-MM-DD HH:MM:SS" (common SQL format) replace space with T
        if (/\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/.test(dateStr)) {
          dateStr = dateStr.replace(" ", "T");
        }
        // If it doesn't have Z or offset, assume UTC and append Z
        if (!/Z|[+-]\d{2}:?\d{2}$/.test(dateStr)) {
          dateStr += "Z";
        }
        date = new Date(dateStr);
      }

      if (isNaN(date.getTime())) return "";

      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;
      const displayMinutes = String(minutes).padStart(2, "0");

      return `${displayHours}:${displayMinutes} ${ampm}`;
    }

    protected startCallFromWave = () => {
      this.dispatchEvent(
        new CustomEvent("start-call", { bubbles: true, composed: true })
      );
    };

    protected async copyMessageText(text: string, messageId: string) {
      const value = String(text || "");
      if (!value.trim()) return;
      try {
        await navigator.clipboard?.writeText(value);
      } catch {
        try {
          const el = document.createElement("textarea");
          el.value = value;
          el.style.position = "fixed";
          el.style.opacity = "0";
          document.body.appendChild(el);
          el.select();
          document.execCommand("copy");
          document.body.removeChild(el);
        } catch {}
      }

      this.copiedMessageId = messageId;
      this.dispatchEvent(
        new CustomEvent("show-toast", {
          detail: { level: "success", text: "Copied" },
          bubbles: true,
          composed: true,
        })
      );

      setTimeout(() => {
        if (this.copiedMessageId === messageId) {
          this.copiedMessageId = null;
        }
      }, 2000);
    }

    protected onAudioPlay = (e: Event) => {
      const audioEl = e.target as HTMLAudioElement;
      if (audioEl.tagName !== "AUDIO") return;

      const path = e.composedPath();
      const audioPlayer = path.find(
        (el) => el instanceof HTMLElement && el.tagName === "HOLLO-AUDIO-PLAYER"
      ) as any;

      if (audioPlayer && audioPlayer.forMessageId) {
        this.playingAudioMessageId = audioPlayer.forMessageId;
        this.audioMessageStates = {
          ...this.audioMessageStates,
          [audioPlayer.forMessageId]: "playing",
        };
        this.requestUpdate();
      }
    };

    protected onAudioPause = (e: Event) => {
      const audioEl = e.target as HTMLAudioElement;
      if (audioEl.tagName !== "AUDIO") return;

      const path = e.composedPath();
      const audioPlayer = path.find(
        (el) => el instanceof HTMLElement && el.tagName === "HOLLO-AUDIO-PLAYER"
      ) as any;

      if (audioPlayer && audioPlayer.forMessageId) {
        const isEnded = audioEl.ended;
        if (this.playingAudioMessageId === audioPlayer.forMessageId) {
          this.playingAudioMessageId = null;
        }
        this.audioMessageStates = {
          ...this.audioMessageStates,
          [audioPlayer.forMessageId]:
            isEnded || audioEl.currentTime === 0 ? "none" : "paused",
        };
        this.requestUpdate();
      }
    };

    protected handleUpdated(changed: Map<string, unknown>) {
      if (changed.has("messages") || changed.has("typing")) {
        // Only auto-scroll if we're on the chat page (has messages), not on suggestions page
        // Check if we have non-greeting messages to determine if we're on chat page
        const hasNonGreetingMessages = this.shouldAutoScroll();
        if (hasNonGreetingMessages) {
          const scroller = this.shadowRoot?.querySelector(
            ".hollo-messages"
          ) as HTMLElement | null;
          if (scroller) {
            requestAnimationFrame(() => {
              scroller.scrollTo({
                top: scroller.scrollHeight,
                behavior: "smooth",
              });
            });
          }
        }
      }
      if (changed.has("showRecorder") && this.showRecorder) {
        this.updateWaveformLevels();
      }
      if (changed.has("messages")) {
        this.checkAudioPlayingState();
      }
    }

    // Override this method in child classes to customize scroll behavior
    // Default: scroll if there are any messages
    protected shouldAutoScroll(): boolean {
      // Check if messages property exists and has items
      const messages = (this as any).messages;
      if (!messages || !Array.isArray(messages)) return false;
      return messages.length > 0;
    }

    protected handleConnected() {
      document.addEventListener("play", this.onAudioPlay, true);
      document.addEventListener("pause", this.onAudioPause, true);
      this.audioCheckInterval = window.setInterval(() => {
        this.checkAudioPlayingState();
      }, 300);
    }

    protected handleDisconnected() {
      document.removeEventListener("play", this.onAudioPlay, true);
      document.removeEventListener("pause", this.onAudioPause, true);
      if (this.audioCheckInterval) {
        clearInterval(this.audioCheckInterval);
        this.audioCheckInterval = undefined;
      }
    }
  }

  return LandingMixinClass as T & typeof LandingMixinClass;
};

// Helper type for mixin constructor
type Constructor<T = {}> = new (...args: any[]) => T;
